export type OAuthProvider = 'discord' | 'google' | 'apple';
const KNOWN: readonly OAuthProvider[] = ['discord', 'google', 'apple'];

export interface PlatformConfig {
  supabaseUrl: string;
  supabasePublishableKey: string;
  /** OAuth providers to offer. Only list ones configured in the Supabase dashboard. */
  authProviders: OAuthProvider[];
  /** True for the pages.dev build (`pnpm build:sandbox`, Vite mode "sandbox"): shows the sandbox notice. */
  sandbox: boolean;
  /** Error reporting; undefined when VITE_SENTRY_DSN is not set. */
  monitoring?: MonitoringConfig;
}

export type MonitoringEnvironment = 'production' | 'sandbox' | 'development';

export interface MonitoringConfig {
  dsn: string;
  environment: MonitoringEnvironment;
  /** The commit the bundle was built from (VITE_RELEASE, set from GITHUB_SHA in CI). */
  release?: string;
}

/** Sentry environment tag for a Vite mode: anything that is not a sandbox or production build is development. */
export function monitoringEnvironment(mode: string | undefined): MonitoringEnvironment {
  return mode === 'production' || mode === 'sandbox' ? mode : 'development';
}

/** Parse VITE_AUTH_PROVIDERS ("discord,google") into a validated, de-duplicated list. */
export function parseProviders(value: string | undefined): OAuthProvider[] {
  const wanted = (value ?? '').split(',').map((v) => v.trim().toLowerCase());
  return KNOWN.filter((p) => wanted.includes(p));
}

/** Build the config from Vite env values; fails loudly when the app is misconfigured. */
export function configFromEnv(env: Record<string, string | undefined>): PlatformConfig {
  const supabaseUrl = env.VITE_SUPABASE_URL;
  const supabasePublishableKey = env.VITE_SUPABASE_PUBLISHABLE_KEY;
  if (!supabaseUrl || !supabasePublishableKey) {
    throw new Error('Missing VITE_SUPABASE_URL or VITE_SUPABASE_PUBLISHABLE_KEY (see .env.development)');
  }
  return {
    supabaseUrl,
    supabasePublishableKey,
    authProviders: parseProviders(env.VITE_AUTH_PROVIDERS),
    sandbox: env.MODE === 'sandbox',
    monitoring: env.VITE_SENTRY_DSN
      ? {
          dsn: env.VITE_SENTRY_DSN,
          environment: monitoringEnvironment(env.MODE),
          release: env.VITE_RELEASE || undefined,
        }
      : undefined,
  };
}
